/**
 * Pure H OS · Statement of Account workspace.
 * Owner+Admin (+Manager, Holding Owner) — list every customer + drill into
 * their canonical statement. Reuses the backend builder for all data;
 * this page never re-computes ledger arithmetic.
 */
import { useEffect, useMemo, useState } from "react";
import { Card, SectionHeader, Button } from "../../components/lib";
import { TabBar, ListRow, Drawer } from "../../components/lib.overlay";
import Breadcrumbs from "../../components/Breadcrumbs";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pureh`;

type Customer = {
  id: string; name: string; segment?: string; tier?: string;
  ventureId?: string; creditLimit?: Record<string, number>;
  openingBalances?: Record<string, number>;
};

type Txn = {
  kind: string; date: string; currency: string;
  docType: string; docNumber: string; description: string;
  debit: number; credit: number; runningBalance: number;
  status: string; reversed: boolean; dueDate: string | null;
  reference: string | null; linked: Record<string, unknown>;
  sourceId: string;
};

type Aging = { current: number; d1_30: number; d31_60: number; d61_90: number; d90_plus: number; total: number; overdue: number; };

type Statement = {
  customer: Customer;
  currencies: string[];
  transactions: Txn[];
  summary: {
    byCurrency: Record<string, { opening: number; debits: number; credits: number; closing: number; invariantOk: boolean; }>;
    unallocated: Record<string, number>;
    lastPaymentAt: string | null;
    availableCredit: Record<string, number>;
    creditLimit: Record<string, number>;
    outstandingByCurrency: Record<string, number>;
  };
  aging: Record<string, Aging>;
  loyalty: {
    currentTier: string; lifetimeSpendEGP: number; currentYearSpendEGP: number;
    nextTier: string | null; gapToNextEGP: number;
    memberSince: string | null; lastOrderAt: string | null;
  } | null;
  period: { from: string | null; to: string | null };
  generatedAt: string;
};

const money = (n: number, ccy = "") => {
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  return `${ccy ? ccy + " " : ""}${sign}${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const KIND_LABEL: Record<string, string> = {
  opening_balance: "Opening balance", sales_invoice: "Sales invoice",
  customer_payment: "Payment", credit_note: "Credit note",
  debit_note: "Debit note", return: "Return", refund: "Refund",
};

type FilterKind = "all" | "invoice" | "payment" | "credit" | "debit" | "return" | "refund" | "reversed" | "open";

function passesFilter(t: Txn, kind: FilterKind, ccy: string | null, dateFrom: string, dateTo: string): boolean {
  if (kind !== "all") {
    if (kind === "reversed" && !t.reversed) return false;
    if (kind === "invoice" && t.docType !== "sales_invoice") return false;
    if (kind === "payment" && t.docType !== "customer_payment") return false;
    if (kind === "credit" && t.docType !== "credit_note") return false;
    if (kind === "debit"  && t.docType !== "debit_note")  return false;
    if (kind === "return" && t.docType !== "return")      return false;
    if (kind === "refund" && t.docType !== "refund")      return false;
    if (kind === "open"   && !(t.docType === "sales_invoice" && (t.status === "sent" || t.status === "overdue" || t.status === "partial"))) return false;
  }
  if (ccy && t.currency !== ccy) return false;
  if (dateFrom && t.date < dateFrom) return false;
  if (dateTo   && t.date > dateTo)   return false;
  return true;
}

export default function StatementOfAccount() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [stmt, setStmt] = useState<Statement | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterKind>("all");
  const [ccy, setCcy] = useState<string | null>(null);
  const [df, setDf] = useState<string>("");
  const [dt, setDt] = useState<string>("");
  const [emailTo, setEmailTo] = useState<string>("");

  useEffect(() => {
    fetch(`${API}/data/customers`, { credentials: "include" })
      .then((r) => r.json())
      .then((rows) => setCustomers(Array.isArray(rows) ? rows : []))
      .catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    if (!openId) { setStmt(null); return; }
    setLoading(true);
    const qs = new URLSearchParams();
    if (df) qs.set("period_from", df);
    if (dt) qs.set("period_to", dt);
    fetch(`${API}/customers/${openId}/statement?${qs.toString()}`, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) throw new Error(await r.text());
        return r.json();
      })
      .then(setStmt)
      .catch((e) => { toast.error(`Failed to load statement: ${String(e).slice(0, 120)}`); setStmt(null); })
      .finally(() => setLoading(false));
  }, [openId, df, dt]);

  const filteredTxns = useMemo(() => {
    if (!stmt) return [];
    return stmt.transactions.filter((t) => passesFilter(t, filter, ccy, df, dt));
  }, [stmt, filter, ccy, df, dt]);

  const openExport = (fmt: "preview" | "pdf" | "xlsx" | "csv") => {
    if (!openId) return;
    const qs = new URLSearchParams();
    if (df) qs.set("period_from", df);
    if (dt) qs.set("period_to", dt);
    if (fmt === "preview") {
      // Fetch the HTML preview payload and open it in a new tab.
      fetch(`${API}/customers/${openId}/statement/preview?${qs.toString()}`, { credentials: "include" })
        .then((r) => r.json())
        .then((d) => {
          const w = window.open("", "_blank");
          if (w) { w.document.open(); w.document.write(d.html); w.document.close(); }
        })
        .catch((e) => toast.error(`Preview failed: ${e}`));
    } else {
      // Direct download via <a> click with credentials
      const a = document.createElement("a");
      a.href = `${API}/customers/${openId}/statement/${fmt}?${qs.toString()}`;
      a.download = "";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  const doEmail = async () => {
    if (!openId) return;
    if (!emailTo.trim()) { toast.error("Enter recipient email"); return; }
    const r = await fetch(`${API}/customers/${openId}/statement/email`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ recipient: emailTo, periodFrom: df || null, periodTo: dt || null }),
    });
    const d = await r.json();
    if (r.status === 202) toast.success(d.message || "Email request queued");
    else toast.error(d.detail || `HTTP ${r.status}`);
  };

  const current = openId ? customers.find((c) => c.id === openId) || stmt?.customer || null : null;

  return (
    <div className="space-y-6" data-testid="soa-workspace">
      <Breadcrumbs />
      <Card testId="soa-header">
        <SectionHeader eyebrow="Accounts Receivable" title="Customer Statements of Account" />
        <div className="text-xs mt-1" style={{ color: "var(--ph-muted)" }}>
          Canonical ledger per customer · balances per currency · open items, aging, loyalty.
        </div>
      </Card>

      <Card padded={false} testId="soa-list">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Customers" title={`${customers.length} customer${customers.length === 1 ? "" : "s"}`} />
        </div>
        <div className="p-2">
          {customers.map((c) => (
            <ListRow
              key={c.id}
              testId={`soa-cust-${c.id}`}
              onClick={() => setOpenId(c.id)}
              label={<span>{c.name}</span>}
              sublabel={<>{c.segment || "—"} · {c.tier || "—"} · credit {money(Object.values(c.creditLimit || {})[0] || 0)} EGP</>}
              trailing={<span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--ph-muted)" }}>Open →</span>}
            />
          ))}
        </div>
      </Card>

      <Drawer
        open={!!openId}
        onClose={() => setOpenId(null)}
        testId="soa-drawer"
        title={current ? current.name : "Statement"}
      >
        {loading && <div className="text-sm" style={{ color: "var(--ph-muted)" }}>Loading…</div>}
        {!loading && stmt && current && (
          <div className="space-y-5" data-testid={`soa-drawer-${current.id}`}>
            {/* Loyalty header */}
            {stmt.loyalty && (
              <div className="rounded p-3 grid grid-cols-2 gap-2 text-xs" style={{ background: "var(--ph-warning-tint)", border: "1px solid var(--ph-warning)" }} data-testid="soa-loyalty">
                <div><b>Tier:</b> {stmt.loyalty.currentTier}</div>
                <div><b>Lifetime:</b> EGP {stmt.loyalty.lifetimeSpendEGP.toLocaleString()}</div>
                <div><b>Current-year:</b> EGP {stmt.loyalty.currentYearSpendEGP.toLocaleString()}</div>
                <div><b>Next:</b> {stmt.loyalty.nextTier || "Top tier"}{stmt.loyalty.nextTier && ` · gap EGP ${stmt.loyalty.gapToNextEGP.toLocaleString()}`}</div>
                {stmt.loyalty.memberSince && <div><b>Member since:</b> {stmt.loyalty.memberSince}</div>}
                {stmt.loyalty.lastOrderAt && <div><b>Last order:</b> {stmt.loyalty.lastOrderAt}</div>}
              </div>
            )}

            {/* Per-currency summary cards */}
            <div className="grid grid-cols-2 gap-3" data-testid="soa-summary">
              {Object.entries(stmt.summary.byCurrency).map(([c, s]) => (
                <div key={c} className="p-3 rounded" style={{ background: "var(--ph-surface-well)", border: "1px solid var(--ph-border-subtle)" }} data-testid={`soa-ccy-${c}`}>
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--ph-muted)" }}>{c} · closing</div>
                  <div className="text-lg font-medium tabular-nums" style={{ color: "var(--ph-text)" }}>{money(s.closing)}</div>
                  <div className="text-[10px] mt-1" style={{ color: "var(--ph-muted)" }}>
                    Opening {money(s.opening)} · Dr {money(s.debits)} · Cr {money(s.credits)}
                    {!s.invariantOk && <span style={{ color: "var(--ph-danger)" }}> · ⚠ invariant mismatch</span>}
                  </div>
                </div>
              ))}
            </div>

            {/* Aging */}
            {Object.keys(stmt.aging).length > 0 && (
              <div data-testid="soa-aging">
                <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--ph-muted)" }}>Aging · open invoices</div>
                <table className="w-full text-xs tabular-nums">
                  <thead><tr style={{ color: "var(--ph-muted)" }}>
                    <th className="text-left py-1">Ccy</th><th className="text-right">Current</th><th className="text-right">1–30</th>
                    <th className="text-right">31–60</th><th className="text-right">61–90</th>
                    <th className="text-right">90+</th><th className="text-right">Total</th>
                  </tr></thead>
                  <tbody>
                  {Object.entries(stmt.aging).map(([c, a]) => (
                    <tr key={c} data-testid={`soa-aging-${c}`} style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
                      <td className="py-1">{c}</td>
                      <td className="text-right">{money(a.current)}</td>
                      <td className="text-right">{money(a.d1_30)}</td>
                      <td className="text-right">{money(a.d31_60)}</td>
                      <td className="text-right">{money(a.d61_90)}</td>
                      <td className="text-right">{money(a.d90_plus)}</td>
                      <td className="text-right font-medium">{money(a.total)}</td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Filters */}
            <div className="space-y-2" data-testid="soa-filters">
              <TabBar<FilterKind>
                testId="soa-filter-tabs"
                active={filter}
                onChange={setFilter}
                tabs={[
                  { key: "all",      label: `All ${stmt.transactions.length}` },
                  { key: "open",     label: "Open" },
                  { key: "invoice",  label: "Invoices" },
                  { key: "payment",  label: "Payments" },
                  { key: "credit",   label: "Credit notes" },
                  { key: "debit",    label: "Debit notes" },
                  { key: "return",   label: "Returns" },
                  { key: "refund",   label: "Refunds" },
                  { key: "reversed", label: "Reversed" },
                ]}
              />
              <div className="flex gap-2 items-center text-xs" style={{ color: "var(--ph-muted)" }}>
                <label>From <input type="date" value={df} onChange={(e) => setDf(e.target.value)} className="ml-1 px-1 py-0.5 rounded border" data-testid="soa-date-from" style={{ borderColor: "var(--ph-border-subtle)" }} /></label>
                <label>To <input type="date" value={dt} onChange={(e) => setDt(e.target.value)} className="ml-1 px-1 py-0.5 rounded border" data-testid="soa-date-to" style={{ borderColor: "var(--ph-border-subtle)" }} /></label>
                <label>Currency
                  <select value={ccy || ""} onChange={(e) => setCcy(e.target.value || null)} className="ml-1 px-1 py-0.5 rounded border" data-testid="soa-ccy-filter" style={{ borderColor: "var(--ph-border-subtle)" }}>
                    <option value="">All</option>
                    {stmt.currencies.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
              </div>
            </div>

            {/* Ledger */}
            <div data-testid="soa-ledger">
              <table className="w-full text-xs tabular-nums">
                <thead>
                  <tr style={{ color: "var(--ph-muted)" }}>
                    <th className="text-left py-1">Date</th>
                    <th className="text-left">Type</th>
                    <th className="text-left">Number</th>
                    <th className="text-left">Description</th>
                    <th className="text-left">Ccy</th>
                    <th className="text-right">Debit</th>
                    <th className="text-right">Credit</th>
                    <th className="text-right">Running</th>
                    <th className="text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTxns.map((t, i) => (
                    <tr key={`${t.sourceId}-${i}`} data-testid={`soa-txn-${t.sourceId}`} style={{ borderTop: "1px solid var(--ph-border-subtle)", opacity: t.reversed ? 0.65 : 1 }}>
                      <td className="py-1 whitespace-nowrap">{t.date}</td>
                      <td>{KIND_LABEL[t.docType] || t.docType}</td>
                      <td className="font-mono text-[10px]">{t.docNumber}</td>
                      <td>{t.description}</td>
                      <td>{t.currency}</td>
                      <td className="text-right">{t.debit ? money(t.debit) : ""}</td>
                      <td className="text-right">{t.credit ? money(t.credit) : ""}</td>
                      <td className="text-right">{money(t.runningBalance)}</td>
                      <td>
                        {t.reversed
                          ? <span className="text-[10px] uppercase" style={{ color: "var(--ph-danger)" }}>reversed</span>
                          : <span className="text-[10px]" style={{ color: "var(--ph-muted)" }}>{t.status}</span>}
                      </td>
                    </tr>
                  ))}
                  {filteredTxns.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-4 text-xs" style={{ color: "var(--ph-muted)" }}>No transactions match the current filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Unallocated */}
            {Object.keys(stmt.summary.unallocated || {}).length > 0 && (
              <div data-testid="soa-unallocated">
                <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--ph-muted)" }}>Unallocated payments</div>
                <div className="flex gap-3 text-xs">
                  {Object.entries(stmt.summary.unallocated).map(([c, v]) => (
                    <span key={c} className="px-2 py-1 rounded" style={{ background: "var(--ph-info-tint)", color: "var(--ph-info)" }}>{c} {money(v)}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Action bar */}
            <div className="flex flex-wrap gap-2 pt-3" style={{ borderTop: "1px solid var(--ph-border-subtle)" }} data-testid="soa-actions">
              <Button testId="soa-preview-btn" onClick={() => openExport("preview")}>Preview</Button>
              <Button testId="soa-pdf-btn"     onClick={() => openExport("pdf")}>PDF</Button>
              <Button testId="soa-xlsx-btn"    onClick={() => openExport("xlsx")}>Excel</Button>
              <Button testId="soa-csv-btn"     onClick={() => openExport("csv")}>CSV</Button>
              <Button testId="soa-print-btn"   onClick={() => openExport("preview")}>Print</Button>
              <input type="email" placeholder="Recipient email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="soa-email-input" className="px-2 py-1 rounded text-xs border" style={{ borderColor: "var(--ph-border-subtle)" }} />
              <Button testId="soa-email-btn" onClick={doEmail}>Email to customer</Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
